'use client'

import { LayoutGrid } from 'lucide-react'
import { memo, useMemo } from 'react'

import { legacyPrimaryNav, legacySecondaryNav } from './navConfig'
import { ExternalLink, isRedirectHref, isSectionActive } from './NavLink'
import { NavMenuItem, NavMenuTrigger, navMenuContentCSS } from './NavMenu'
import { useHoverMenu } from './useHoverMenu'
import { useNavOverflow } from './useNavOverflow'
import { HOTKEY_BY_PATH } from '@features/appHotkeys/appHotkeys'
import { Kbd } from '@fp/ui-kit'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@ui/DropDownMenu'

type LegacyNavItem = { title: string; href: string }

const MoreNavMenu: React.FC<{ items: LegacyNavItem[]; pathname: string }> = memo(({ items, pathname }) => {
  const activeItem = items.find((item) => isSectionActive(pathname, item.href))
  const { rootProps, triggerProps, contentProps } = useHoverMenu()

  return (
    <DropdownMenu {...rootProps}>
      <DropdownMenuTrigger asChild>
        <NavMenuTrigger
          icon={LayoutGrid}
          isActive={!!activeItem}
          label={activeItem?.title ?? 'More'}
          aria-label='More pages'
          {...triggerProps}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' sideOffset={0} className={navMenuContentCSS} {...contentProps}>
        {items.map((item) => (
          <NavMenuItem key={item.href} href={item.href} title={item.title} pathname={pathname} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
})
MoreNavMenu.displayName = 'MoreNavMenu'

type ClassicNavProps = { pathname: string; showHotkeys: boolean }

export const ClassicNav: React.FC<ClassicNavProps> = memo(({ pathname, showHotkeys }) => {
  const { navRef, moreRef, registerItem, visibleCount } = useNavOverflow({
    itemCount: legacyPrimaryNav.length,
    gap: 8, // the nav's `gap-2`
    remeasureKey: pathname,
  })

  const moreItems = useMemo(() => [...legacyPrimaryNav.slice(visibleCount), ...legacySecondaryNav], [visibleCount])

  return (
    <nav ref={navRef} className='mr-4 h-20 flex flex-1 min-w-0 items-center justify-start gap-2 overflow-hidden'>
      {legacyPrimaryNav.slice(0, visibleCount).map((item, index) => {
        const isRedirect = isRedirectHref(item.href)
        const hotkey = HOTKEY_BY_PATH[item.href]

        return (
          <div key={`${index}-${item.href}`} ref={registerItem(index)} className='relative'>
            <ExternalLink href={item.href} title={item.title} pathname={pathname} />

            {showHotkeys && !isRedirect && hotkey && (
              <Kbd size='sm' className='absolute bottom-0 left-1/2 -translate-x-1/2'>
                {hotkey}
              </Kbd>
            )}
          </div>
        )
      })}

      <div ref={moreRef}>
        <MoreNavMenu items={moreItems} pathname={pathname} />
      </div>
    </nav>
  )
})
ClassicNav.displayName = 'ClassicNav'
